// Deleting a Linkflare account touches two systems: our backend (DELETE /users) and the
// Clerk login (user.delete()). Clerk refuses user.delete() with
// `session_reverification_required` when the session was not verified recently (the
// instance has reverification on). The old flow never handled that: it showed a
// "partial" toast and routed to /sign-in while still signed in, App.vue bounced the
// signed-in user back to /bookmarks, and that bootstrap made the backend register the
// account again two seconds after deleting it (6 of 9 deletions in Sept 2026).
// See working-material/plans/account-deletion-zombie-accounts.md.
//
// Invariants this module owns:
//   1. If the session needs reverification, ask BEFORE anything is deleted.
//   2. Once the backend data is gone, no authenticated call from this session goes out.
//   3. Once the backend data is gone, the session is signed out, whatever Clerk said.
//
// Dependency-free so the embed bundle and the tests can import it.

export const CLERK_REVERIFICATION_REQUIRED = 'session_reverification_required'

// Clerk's preset for destructive actions: a factor verified within the last 10 minutes.
export const DELETE_REVERIFICATION_LEVEL = 'strict'

export const REVERIFICATION = Object.freeze({
  VERIFIED: 'verified',
  CANCELLED: 'cancelled',
  UNAVAILABLE: 'unavailable'
})

export const DELETION_OUTCOME = Object.freeze({
  CANCELLED: 'cancelled',
  DELETED: 'deleted',
  PARTIAL: 'partial'
})

const SUSPEND_ALL = Symbol('suspend-all-sessions')
let suspendedSessionId = null

/**
 * Stop handing out tokens for this Clerk session. The page reloads right after a
 * deletion, which clears this; until then every poll and bootstrap call gets no token,
 * so none of them can reach the backend as the deleted user. Unknown id: suspend all.
 */
export function suspendSessionTokens(sessionId) {
  suspendedSessionId = sessionId || SUSPEND_ALL
}

export function areSessionTokensSuspended(sessionId) {
  if (suspendedSessionId === null) return false
  return suspendedSessionId === SUSPEND_ALL || suspendedSessionId === sessionId
}

export function resetSessionTokenSuspension() {
  suspendedSessionId = null
}

/** Clerk surfaces a FAPI refusal as ClerkAPIResponseError with an `errors[]` of codes. */
export function isReverificationRequired(error) {
  return Array.isArray(error?.errors) && error.errors.some((e) => e?.code === CLERK_REVERIFICATION_REQUIRED)
}

/**
 * Local check against the session's factor-verification age (no network). An unknown
 * answer counts as "fine": the Clerk delete below still catches a real refusal.
 */
export function sessionNeedsReverification(session) {
  try {
    if (typeof session?.checkAuthorization !== 'function') return false
    return session.checkAuthorization({ reverification: DELETE_REVERIFICATION_LEVEL }) !== true
  } catch (e) {
    return false
  }
}

/**
 * Opens Clerk's own reverification modal, the one its React `useReverification` hook
 * uses (@clerk/vue 1.x does not wrap it). Resolves with a REVERIFICATION value.
 */
export function openReverification(clerk) {
  const open = clerk?.__internal_openReverification
  if (typeof open !== 'function') return Promise.resolve(REVERIFICATION.UNAVAILABLE)
  return new Promise((resolve) => {
    try {
      open.call(clerk, {
        afterVerification: () => resolve(REVERIFICATION.VERIFIED),
        afterVerificationCancelled: () => resolve(REVERIFICATION.CANCELLED)
      })
    } catch (e) {
      resolve(REVERIFICATION.UNAVAILABLE)
    }
  })
}

/**
 * Runs the whole deletion. Throws only when the backend delete fails, in which case
 * nothing was deleted and the caller shows the error.
 *
 * @param {object} deps
 * @param {() => boolean} deps.needsReverification
 * @param {() => Promise<string>} deps.reverify resolves with a REVERIFICATION value
 * @param {() => Promise<void>} deps.deleteBackendAccount
 * @param {() => void} deps.onBackendDeleted suspend tokens, clear local state
 * @param {() => Promise<void>} deps.deleteClerkUser
 * @param {(outcome: string) => Promise<void>} deps.signOut may navigate away and never resolve
 * @returns {Promise<{outcome: string, error?: unknown}>}
 */
export async function deleteAccountEverywhere({
  needsReverification,
  reverify,
  deleteBackendAccount,
  onBackendDeleted,
  deleteClerkUser,
  signOut
}) {
  if (needsReverification()) {
    const answer = await reverify()
    if (answer === REVERIFICATION.CANCELLED) {
      return { outcome: DELETION_OUTCOME.CANCELLED }
    }
    // UNAVAILABLE: go on, the Clerk delete below may still be accepted
  }

  await deleteBackendAccount()

  try {
    onBackendDeleted()
  } catch (e) {
    // local cleanup must never keep the session signed in
  }

  let clerkError = null
  try {
    await deleteClerkUser()
  } catch (error) {
    clerkError = error
    if (isReverificationRequired(error) && (await reverify()) === REVERIFICATION.VERIFIED) {
      try {
        await deleteClerkUser()
        clerkError = null
      } catch (retryError) {
        clerkError = retryError
      }
    }
  }

  const outcome = clerkError ? DELETION_OUTCOME.PARTIAL : DELETION_OUTCOME.DELETED
  try {
    await signOut(outcome)
  } catch (e) {
    // the caller's signOut owns its fallback navigation
  }
  return clerkError ? { outcome, error: clerkError } : { outcome }
}

// One-shot handoff to the sign-in page. Clerk's signOut does a full page load, so an
// in-app toast would be lost. sessionStorage rather than a query param, so a crafted
// /sign-in link cannot show someone an "account deleted" message.
const OUTCOME_KEY = 'linkflare_account_deletion_outcome'

export function rememberDeletionOutcome(outcome) {
  try { sessionStorage.setItem(OUTCOME_KEY, outcome) } catch (e) { /* private mode */ }
}

export function takeDeletionOutcome() {
  try {
    const outcome = sessionStorage.getItem(OUTCOME_KEY)
    sessionStorage.removeItem(OUTCOME_KEY)
    return outcome === DELETION_OUTCOME.DELETED || outcome === DELETION_OUTCOME.PARTIAL ? outcome : null
  } catch (e) {
    return null
  }
}
